import * as z from 'zod';

/**
 * `GET /vendors/me` — the merchant's own profile on the OLD backend. Shape
 * captured from a live response; the dashboard reads `balance.current` and
 * `new_client_count` from it, and the profile screen will read the rest.
 *
 * Note the backend's own typo, `emploee_profile`, is preserved: renaming it
 * here would silently drop the field when an employee account is active.
 */
export const legacyBalanceSchema = z.object({
  /** Current balance in tiyin-less soum; may be negative (debt). */
  current: z.number().default(0),
  /** Threshold under which ordering is blocked when `restriction_enabled`. */
  minimal: z.number().default(0),
  restriction_enabled: z.boolean().default(false),
});

export const legacyContractSchema = z.object({
  number: z.string().nullable().default(null),
  start_date: z.string().nullable().default(null),
  document: z.string().nullable().default(null),
});

export const legacyCallbackSchema = z.object({
  url: z.string().nullable().default(null),
  token: z.string().nullable().default(null),
});

/**
 * `legal_form` (`v1LegalForm` in merchant-profile, `openapi-merchant-profile.json`).
 * The brand's legal form as a named enum rather than the legacy `is_corporate`
 * boolean: `COMPANY` is `is_corporate = true`, `INDIVIDUAL` is `false`.
 * Unknown future values (e.g. a sole-proprietor form) degrade to
 * `LEGAL_FORM_UNSPECIFIED` instead of failing the whole profile payload, and
 * callers fall back to `is_corporate` while the form is unspecified — see
 * `legacyIsLegalEntity`.
 */
export const legalFormSchema = z
  .enum(['LEGAL_FORM_UNSPECIFIED', 'LEGAL_FORM_INDIVIDUAL', 'LEGAL_FORM_COMPANY'])
  .catch('LEGAL_FORM_UNSPECIFIED');
export type LegalForm = z.infer<typeof legalFormSchema>;

export const legacyEmployeeProfileSchema = z.object({
  id: z.number().optional(),
  first_name: z.string().nullable().default(null),
  last_name: z.string().nullable().default(null),
  business_name: z.string().nullable().default(null),
  business_logo: z.string().nullable().default(null),
  role: z.string().nullable().default(null),
  token: z.string().nullable().default(null),
  is_corporate: z.boolean().default(false),
  balance: legacyBalanceSchema.optional(),
});

export const legacyProfileSchema = z.object({
  name: z.string().nullable().default(null),
  business_name: z.string().nullable().default(null),
  lang: z.string().nullable().default(null),
  email: z.string().nullable().default(null),
  phone: z.string().nullable().default(null),
  address: z.string().nullable().default(null),
  logo: z.string().nullable().default(null),
  business_logo: z.string().nullable().default(null),
  display_id: z.string().nullable().default(null),
  contract: legacyContractSchema.nullable().default(null),
  balance: legacyBalanceSchema.default(() => legacyBalanceSchema.parse({})),
  is_business: z.boolean().default(false),
  is_corporate: z.boolean().default(false),
  /** Absent (or null) on older backends — callers fall back to `is_corporate`. */
  legal_form: legalFormSchema.nullable().optional(),
  requested_business: z.boolean().default(false),
  door_to_door_required: z.boolean().default(false),
  is_client_full_pay: z.boolean().default(false),
  /** Clients acquired in the current period — the "New Clients" tile. */
  new_client_count: z.number().default(0),
  callback: legacyCallbackSchema.nullable().default(null),
  emploee_profile: legacyEmployeeProfileSchema.nullable().default(null),
});

export type LegacyBalance = z.infer<typeof legacyBalanceSchema>;
export type LegacyProfile = z.infer<typeof legacyProfileSchema>;

/**
 * `POST /vendors/integration/generate-token` — mints a new integration token
 * for the merchant. The response carries the token itself plus whether issuing
 * it revoked the previous one (`revoked_previous`), which the confirm copy
 * warns about.
 */
export const integrationTokenSchema = z.object({
  integration_token: z.string(),
  issued_at: z.string(),
  revoked_previous: z.boolean().default(false),
  token_id: z.string().default(''),
});

export type IntegrationToken = z.infer<typeof integrationTokenSchema>;

/**
 * `GET /vendors/stats` — one row per day for the "Peak Days" bar chart. The
 * endpoint returns a bare array (no envelope) and omits days with no orders,
 * so the chart fills the missing weekdays itself.
 */
export const legacyStatPointSchema = z.object({
  /** Day bucket, an ISO timestamp at midnight UTC (`2026-08-04T00:00:00Z`). */
  date: z.string(),
  /** Orders that day — the bar's height. */
  count: z.number().default(0),
  /** Revenue that day; shown in the bar's tooltip. */
  total_price: z.number().default(0),
});

export const legacyStatsSchema = z.array(legacyStatPointSchema).nullable().default([]);

export type LegacyStatPoint = z.infer<typeof legacyStatPointSchema>;

/**
 * `GET /vendors/invoices` — vendor top-up invoices.
 * Corresponds to `types.Invoice` from the backend.
 */
export const legacyInvoiceSchema = z.object({
  id: z.number(),
  amount: z.number().default(0),
  approved_at: z.string().nullable().default(null),
  approved_by: z.number().nullable().default(null),
  rejected_at: z.string().nullable().default(null),
  rejected_by: z.number().nullable().default(null),
  rejected_reason: z.string().nullable().default(null),
  transaction_id: z.string().nullable().default(null),
  vendor_id: z.number().optional(),
  document: z.string().nullable().default(null),
  created_at: z.string(),
});

export const legacyInvoicesResponseSchema = z.object({
  data: z.array(legacyInvoiceSchema).default([]),
  count: z.number().default(0),
});

export type LegacyInvoice = z.infer<typeof legacyInvoiceSchema>;
export type LegacyInvoicesResponse = z.infer<typeof legacyInvoicesResponseSchema>;
